import { LabelOption } from '@/api/api';
import { FolderContent, FolderContentsSortField, Folder as FolderType, SortOrder, UUID } from '@/api/generatedApi';
import { serializeFilterModel } from '@/utils/columnFilterSerializer';
import { useGetCurrentUser } from '@/hooks/api/users/useGetCurrentUser';
import { useBulkSelection } from '@/hooks/useBulkSelection';
import { useCardGridData } from '@/hooks/useCardGridData';
import { useContextMenu } from '@/hooks/useContextMenu';
import { useGlobalDeleteState } from '@/hooks/useGlobalDeleteState';
import { ViewMode } from '@/hooks/useViewMode';
import { AssetsPageObjectModel } from '@/poms/assetsPageObjectModel';
import { useAssetUploadContext } from '@/services/assetUpload/useAssetUploadContext';
import { createUseStyles } from '@/services/createUseStyles';
import { useTenants } from '@/services/tenants/useTenants';
import { ApiErrorDisplay } from '@/types/apiError';
import { ContextMenuOption, ALL_CONTEXT_MENU_OPTIONS } from '@/types/contextMenuOptions';
import { insertMetadataColumnsAfterDetails } from '@/utils/metadata';
import { NegationFilterPanel } from '@/components/Search/SearchTable/NegationFilterPanel';
import { alpha, Box, Theme } from '@mui/material';
import { keyframes } from '@mui/system';
import {
  useGridApiRef,
  GridApiPro,
  GridColDef,
  GridRowClassNameParams,
  GridSortModel,
  GridColumnVisibilityModel,
  type GridFilterModel,
} from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { ContentsTableContextMenu } from '@/components/ContextMenu/ContentsTableContextMenu';
import { CardGrid, getSavedCardPageCount } from '../CardGrid/CardGrid';
import { ContentCard } from '../CardGrid/ContentCard';
import { DataGridLazy } from '../DataGrid/DataGridLazy';
import { DragDropUpload } from '../Upload/DragDropUpload/DragDropUpload';
import { ZeroStateOverlay } from '../ZeroStateUpload/ZeroStateOverlay';
import { ZeroStateUpload } from '../ZeroStateUpload/ZeroStateUpload';
import { getBaseColumns } from './columnDefinitions';
import { DATA_GRID_LAZY_PAGE_SIZE } from '../DataGrid/definitions';
import { DraggableRow } from '../DataGrid/DraggableRow';
import { BulkSelectionProvider } from './BulkSelectionContext';
import { ContentsTableToolbar } from './ContentsTableToolbar';
import { DraggableFolderContentRow } from '../AssetsPage/DraggableFolderContentRow';
import { ContentDragStateValue, useContentDragStateOptional } from '../AssetsPage/ContentDragContext';
import { useScrollPositionRestore } from '@/hooks/useScrollPositionRestore';
import { useGridStateParameters } from '@/hooks/useGridStateParameters';
import { useRowHighlight } from '@/hooks/useRowHighlight';
import { useNavigationType } from 'react-router-dom';

const useStyles = createUseStyles((theme: Theme) => ({
  DataGrid: {
    borderRadius: '6px !important',
    '& .MuiDataGrid-cell': {
      lineHeight: 'unset !important',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
    },
    '& .MuiDataGrid-cellCheckbox': {
      cursor: 'default',
    },
    '& .MuiDataGrid-cell:focus': {
      outline: 'none !important',
    },
    '& .MuiDataGrid-row.folder-drop-target': {
      backgroundColor: `${alpha(theme.palette.info.main, 0.15)} !important`,
      outline: `2px solid ${alpha(theme.palette.info.main, 0.8)} !important`,
      outlineOffset: '-2px',
      '& .MuiDataGrid-cell': {
        backgroundColor: `${alpha(theme.palette.info.main, 0.15)} !important`,
      },
    },
    '& .folder-drop-target': {
      backgroundColor: `${alpha(theme.palette.info.main, 0.15)} !important`,
      outline: `2px solid ${alpha(theme.palette.info.main, 0.8)} !important`,
    },
  },
  tableContainer: {
    display: 'flex',
    width: '100%',
    height: '100%',
    overflow: 'hidden',
  },
  dataGridWrapper: {
    flex: 1,
    minWidth: 0,
    height: '100%',
  },
  errorContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: '100%',
    padding: '24px',
  },
}));

const rowHighlightFade = keyframes`
  0% { background-color: var(--physna-row-highlight-color); }
  50% { background-color: var(--physna-row-highlight-color); }
  100% { background-color: transparent; }
`;

const DragStateSync = ({ stateRef }: { stateRef: React.RefObject<ContentDragStateValue> }): React.ReactNode => {
  const state = useContentDragStateOptional();
  stateRef.current = state
    ? { dropTargetFolderId: state.dropTargetFolderId, draggedContents: state.draggedContents }
    : { dropTargetFolderId: undefined, draggedContents: [] };
  return undefined;
};

export const ContentsTable = ({
  fetchContents,
  isLoading,
  error,
  currentFolder,
  isFolderEmpty,
  isSearch,
  enableBulkActions = true,
  contextMenuOptions = ALL_CONTEXT_MENU_OPTIONS,
  enableRowDragging = false,
  enableFolderDragging = false,
  onRowContextMenu,
  gridApiRef,
  customBaseColumns,
  metadataColumns,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
  isColumnDataLoading,
  showRootFolder,
  showCreateButton = true,
  showFiltersButton = true,
  onRefresh,
  toolbarActions,
  bulkActions,
  viewMode,
  optimisticRemovedContentIds,
  folderContentsVersion = 0,
  labelOptions,
}: {
  fetchContents: (parameters: {
    page: number;
    perPage: number;
  }) => Promise<{ contents: FolderContent[]; pageData: { total: number } }>;
  isLoading?: boolean;
  error?: ApiErrorDisplay;
  currentFolder?: FolderType;
  isFolderEmpty?: boolean;
  isSearch?: boolean;
  enableBulkActions?: boolean;
  contextMenuOptions?: ContextMenuOption[];
  enableRowDragging?: boolean;
  enableFolderDragging?: boolean;
  onRowContextMenu?: (event: React.MouseEvent<HTMLDivElement>) => void;
  gridApiRef?: React.RefObject<GridApiPro | null>;
  customBaseColumns?: GridColDef[];
  metadataColumns?: GridColDef[];
  columnVisibilityModel?: GridColumnVisibilityModel;
  onColumnVisibilityModelChange?: (model: GridColumnVisibilityModel) => void;
  isColumnDataLoading?: boolean;
  showRootFolder?: boolean;
  showCreateButton?: boolean;
  showFiltersButton?: boolean;
  onRefresh?: () => void;
  toolbarActions?: React.ReactNode;
  bulkActions?: React.ReactNode;
  viewMode?: ViewMode;
  optimisticRemovedContentIds?: string[];
  folderContentsVersion?: number;
  labelOptions?: LabelOption[];
}) => {
  const c = useStyles();
  const { getTenantId } = useTenants();
  const dragStateReference = useRef<ContentDragStateValue>({
    dropTargetFolderId: undefined,
    draggedContents: [],
  });

  const { setTargetFolderId, handleFileUpload, handleFolderUpload, dropzoneProps } = useAssetUploadContext();

  useEffect(() => {
    setTargetFolderId(currentFolder?.id);
  }, [currentFolder?.id, setTargetFolderId]);

  const { permissions } = useGetCurrentUser();
  const { isAnyDeletePending } = useGlobalDeleteState();

  const internalGridApiReference = useGridApiRef();
  const gridApi = gridApiRef ?? internalGridApiReference;

  const navigationType = useNavigationType();
  useScrollPositionRestore({
    apiReference: gridApi,
    folderKey: currentFolder?.id ?? 'root',
    shouldRestore: navigationType === 'POP',
  });

  const { sortModel, filterModel, onSortModelChange, onFilterModelChange, onNegationChange } = useGridStateParameters();

  const highlightId = useRowHighlight(gridApi);

  // Suppress filter model changes while metadata columns are still loading.
  // The grid strips filter items for columns it doesn't recognize yet, which
  // would erase metadata filters from the URL before they can be restored.
  const stableFilterModelChange = useCallback(
    (newModel: GridFilterModel) => {
      if (isColumnDataLoading) return;
      onFilterModelChange(newModel);
    },
    [isColumnDataLoading, onFilterModelChange]
  );

  const { selectedRows, isLoadingAll, handleSelectionChange, clearSelection } = useBulkSelection<
    FolderContent,
    { contents: FolderContent[] }
  >({
    fetchContents,
    dataKey: 'contents',
    pageSize: DATA_GRID_LAZY_PAGE_SIZE,
    gridApi,
  });

  useEffect(() => {
    clearSelection();
  }, [currentFolder?.id]);

  const hasContextMenu = contextMenuOptions.length > 0;

  const canUseBulkActionsSidebar = enableBulkActions && [permissions.canDeleteAssets].some(Boolean);
  const canUseDragDropUpload =
    contextMenuOptions.includes('create') && !isSearch && [permissions.canUploadAssets].some(Boolean);

  const allowedContextMenuOptions = contextMenuOptions.filter(option => {
    if (option === 'create') {
      return [permissions.canUploadAssets, permissions.canCreateFolders].some(Boolean);
    }
    if (option === 'delete') {
      return [permissions.canDeleteAssets, permissions.canDeleteFolders].some(Boolean);
    }
    return true;
  });

  const isCardMode = viewMode === 'card';

  const cardGridResetKey = `${viewMode}:${currentFolder?.id ?? 'root'}:${JSON.stringify(filterModel)}:${JSON.stringify(sortModel)}:${folderContentsVersion}`;

  const tenantId = getTenantId();

  const fetchContentsReference = useRef(fetchContents);
  fetchContentsReference.current = fetchContents;

  const fetchCardPage = useCallback(
    async (parameters: { page: number; perPage: number }) => {
      if (!isCardMode || !tenantId) {
        return { items: [] as FolderContent[], total: 0 };
      }

      const SORT_FIELD_MAP: Record<string, FolderContentsSortField> = {
        name: FolderContentsSortField.FolderNameAndAssetPath,
        creator: FolderContentsSortField.CreatedBy,
        createdAt: FolderContentsSortField.CreatedAt,
      };

      const validSort = sortModel?.[0];
      const sortField = validSort?.field;
      const isMetadataSort = sortField?.startsWith('metadata.');
      const sortBy = sortField && !isMetadataSort ? SORT_FIELD_MAP[sortField] : undefined;
      const sortByMetadataField = sortField && isMetadataSort ? sortField.slice('metadata.'.length) : undefined;
      const sortOrder = validSort?.sort ? (validSort.sort.toUpperCase() as SortOrder) : undefined;
      const filters = serializeFilterModel(filterModel);

      const result = await fetchContentsReference.current({
        ...parameters,
        sortBy,
        sortOrder,
        sortByMetadataField,
        filters,
      } as { page: number; perPage: number });
      return { items: result.contents, total: result.pageData.total };
    },
    [sortModel, filterModel, isCardMode, tenantId]
  );

  const cardScrollKey = `assets:${currentFolder?.id ?? 'root'}`;
  const cardGridData = useCardGridData<FolderContent>({
    fetchPage: fetchCardPage,
    resetKey: cardGridResetKey,
    initialPageCount: navigationType === 'POP' ? getSavedCardPageCount(cardScrollKey) : 0,
    optimisticRemovedIds: optimisticRemovedContentIds,
  });

  const cardGridItemsReference = useRef(cardGridData.items);
  cardGridItemsReference.current = cardGridData.items;

  const {
    contextMenuPosition,
    contextMenuClickedItem,
    contextMenuClickedField,
    handleGridContextMenu,
    handleCloseContextMenu,
  } = useContextMenu<FolderContent>({
    canUseContextMenu: hasContextMenu,
    getRowById: (rowId: UUID) => {
      const gridRow = gridApi.current?.getRow<FolderContent>(rowId);
      if (gridRow) return gridRow;
      return cardGridItemsReference.current.find(item => item.id === rowId);
    },
  });

  const handleGridContextMenuReference = useRef(handleGridContextMenu);
  handleGridContextMenuReference.current = handleGridContextMenu;

  const stableContextMenuHandler = useCallback(
    (event: React.MouseEvent) => handleGridContextMenuReference.current(event),
    []
  );

  const renderCard = useCallback(
    (item: FolderContent) => <ContentCard content={item} onContextMenu={stableContextMenuHandler} />,
    [stableContextMenuHandler]
  );
  const getItemKey = useCallback((item: FolderContent) => item.id, []);

  const getRowClassName = (parameters: GridRowClassNameParams<FolderContent>) => {
    const classes: string[] = [];

    if (highlightId && parameters.row?.id === highlightId) {
      classes.push('row-highlight');
    }

    const { dropTargetFolderId, draggedContents } = dragStateReference.current;
    if (enableFolderDragging && dropTargetFolderId && draggedContents.length > 0) {
      const row = parameters.row;
      if (row?.contentType === 'folder') {
        const isBeingDragged = draggedContents.some(c => c.id === row.id);
        if (row.id === dropTargetFolderId && !isBeingDragged) {
          classes.push('folder-drop-target');
        }
      }
    }

    return classes.join(' ');
  };

  const columns = useMemo(() => {
    const columnsToUse = customBaseColumns ?? getBaseColumns({ labelOptions });
    return insertMetadataColumnsAfterDetails(columnsToUse, metadataColumns ?? []);
  }, [customBaseColumns, metadataColumns, labelOptions]);

  if (!tenantId) {
    return;
  }

  const gridContent = (
    <DragDropUpload dropzoneProps={dropzoneProps} disabled={!canUseDragDropUpload}>
      <Box
        className={c.dataGridWrapper}
        onContextMenu={isCardMode ? undefined : (onRowContextMenu ?? handleGridContextMenu)}
        data-testid={AssetsPageObjectModel.DataGridWrapper}
        sx={isCardMode ? { display: 'grid', gridTemplate: '1fr / 1fr' } : undefined}
      >
        <BulkSelectionProvider
          value={{
            selectedRows,
            isLoadingAll,
            clearSelection,
            selectionCount: selectedRows.length,
          }}
        >
          <DataGridLazy<FolderContent, { contents: FolderContent[] }>
            key={currentFolder?.id ?? 'root'}
            apiRef={gridApi}
            className={c.DataGrid}
            columns={columns}
            sx={theme => ({
              '--physna-row-highlight-color': alpha(theme.palette.primary.main, 0.45),
              '& .MuiBadge-root .MuiBadge-badge': { display: 'none' },
              '& .MuiDataGrid-row.row-highlight': {
                animation: `${rowHighlightFade} 2s ease-out`,
                outline: `2px solid ${theme.palette.primary.main}`,
                outlineOffset: '-2px',
                '& .MuiDataGrid-cell': {
                  animation: `${rowHighlightFade} 2s ease-out`,
                },
              },
              ...(isCardMode && {
                gridArea: '1 / 1',
                '& .MuiDataGrid-mainContent > :not(:first-of-type)': { display: 'none' },
              }),
            })}
            initialState={{ pinnedColumns: { left: ['name'], right: ['__check__'] } }}
            sortModel={sortModel}
            onSortModelChange={onSortModelChange}
            filterModel={filterModel}
            onFilterModelChange={stableFilterModelChange}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={onColumnVisibilityModelChange}
            fetchRows={fetchContents}
            dataKey='contents'
            rowHeight={80}
            columnHeaderHeight={40}
            loading={isLoading || isColumnDataLoading || isAnyDeletePending}
            checkboxSelection={canUseBulkActionsSidebar}
            onRowSelectionModelChange={handleSelectionChange}
            error={error}
            density='compact'
            getRowClassName={getRowClassName}
            disableRowSelectionOnClick
            hideFooter={isFolderEmpty}
            showToolbar
            slots={{
              toolbar: ContentsTableToolbar,
              filterPanel: NegationFilterPanel,
              noRowsOverlay: canUseDragDropUpload ? ZeroStateOverlay : undefined,
              ...(enableRowDragging && { row: DraggableRow }),
              ...(enableFolderDragging && { row: DraggableFolderContentRow }),
            }}
            slotProps={{
              filterPanel: { onNegationChange },
              toolbar: {
                folderId: currentFolder?.id,
                showRootFolder,
                bulkActions,
                showCreateButton,
                showFiltersButton,
                onRefresh,
                toolbarActions,
                viewMode,
                onSortModelChange,
              } as {
                folderId?: string;
                showRootFolder?: boolean;
                bulkActions?: React.ReactNode;
                showCreateButton?: boolean;
                showFiltersButton?: boolean;
                onRefresh?: () => void;
                toolbarActions?: React.ReactNode;
                viewMode?: ViewMode;
                onSortModelChange?: (model: GridSortModel) => void;
              },
              footer: {
                sx: {
                  minHeight: '27px',
                  padding: '0 10px',
                },
              },
              footerRowCount: { sx: { margin: 0 } },
              row: {
                ...(onRowContextMenu && { onContextMenu: onRowContextMenu }),
                ...(enableFolderDragging && { dragStateRef: dragStateReference }),
              },
            }}
            localeText={{ footerTotalRows: 'Rows:' }}
          />
          {isCardMode && (
            <Box
              sx={{
                gridArea: '1 / 1',
                zIndex: 1,
                overflow: 'hidden',
                pointerEvents: 'none',
                paddingTop: currentFolder ? '80px' : '42px',
                '& > *': { pointerEvents: 'auto' },
              }}
            >
              <CardGrid<FolderContent>
                items={cardGridData.items}
                total={cardGridData.total}
                renderCard={renderCard}
                getItemKey={getItemKey}
                hasNextPage={cardGridData.hasNextPage}
                isFetchingNextPage={cardGridData.isFetchingNextPage}
                fetchNextPage={cardGridData.fetchNextPage}
                isLoading={cardGridData.isLoading || !!isLoading}
                emptyState={canUseDragDropUpload ? <ZeroStateUpload /> : undefined}
                scrollKey={cardScrollKey}
                pageCount={cardGridData.pageCount}
                resetKey={cardGridResetKey}
              />
            </Box>
          )}
        </BulkSelectionProvider>
      </Box>
    </DragDropUpload>
  );

  return (
    <Box className={c.tableContainer}>
      <DragStateSync stateRef={dragStateReference} />
      {gridContent}
      {hasContextMenu && (
        <ContentsTableContextMenu<FolderContent>
          anchorPosition={contextMenuPosition}
          onClose={handleCloseContextMenu}
          clickedItem={contextMenuClickedItem}
          clickedField={contextMenuClickedField}
          columns={columns}
          parentFolder={currentFolder}
          handleFileUpload={handleFileUpload}
          handleFolderUpload={handleFolderUpload}
          setTargetFolderId={setTargetFolderId}
          isSearch={isSearch}
          allowedOptions={allowedContextMenuOptions}
          selectedRows={selectedRows}
          onBulkActionComplete={clearSelection}
        />
      )}
    </Box>
  );
};
